import { useState } from "react";
import { Link } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

const schema = z.object({
  email: z.string().email("Enter a valid email."),
});
type ForgotPasswordForm = z.infer<typeof schema>;

// Sends a password-reset email via supabase.auth.resetPasswordForEmail. The
// recovery link points back at /auth/callback with `next=/reset-password`,
// so the user lands on the new-password page once the recovery session is
// established.
export function ForgotPasswordPage() {
  const [sentTo, setSentTo] = useState<string | null>(null);

  const form = useForm<ForgotPasswordForm>({
    resolver: zodResolver(schema),
    defaultValues: { email: "" },
  });

  async function onSubmit(values: ForgotPasswordForm) {
    const redirect = new URL("/auth/callback", window.location.origin);
    redirect.searchParams.set("next", "/reset-password");
    const { error } = await supabase.auth.resetPasswordForEmail(values.email, {
      redirectTo: redirect.toString(),
    });
    if (error) {
      toast.error(error.message);
      return;
    }
    setSentTo(values.email);
    toast.success("Reset link sent. Check your email.");
  }

  if (sentTo) {
    return (
      <div className="rounded-xl border bg-card p-8 shadow-sm">
        <Alert className="border-accent/30 bg-accent/5">
          <Mail className="h-4 w-4 text-accent" />
          <AlertDescription>
            <div className="font-medium text-foreground">Check your email</div>
            <p className="mt-1 text-muted-foreground">
              We sent a password reset link to{" "}
              <span className="font-medium text-foreground">{sentTo}</span>. It expires in 1 hour.
            </p>
            <Link
              to="/login"
              className="mt-3 inline-block text-xs font-medium text-accent underline-offset-4 hover:underline"
            >
              Back to sign in
            </Link>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="rounded-xl border bg-card p-8 shadow-sm">
      <div className="mb-6 space-y-1.5">
        <h1 className="font-display text-2xl font-semibold tracking-tight">Reset password</h1>
        <p className="text-sm text-muted-foreground">
          Enter your email and we&apos;ll send you a link to set a new password.
        </p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input type="email" autoComplete="email" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button type="submit" disabled={form.formState.isSubmitting} className="w-full">
            {form.formState.isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Send reset link
          </Button>
        </form>
      </Form>

      <p className="mt-6 border-t pt-4 text-center text-xs text-muted-foreground">
        Remember your password?{" "}
        <Link to="/login" className="font-medium text-foreground underline-offset-4 hover:underline">
          Back to sign in
        </Link>
      </p>
    </div>
  );
}
